import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Loader2, Users2, Check, Ban, ShieldCheck, Eye } from "lucide-react";

const EMPTY = { name: "", role: "viewer", email: "", username: "", password: "" };

export default function Config() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [na, setNa] = useState(EMPTY);
  const [creating, setCreating] = useState(false);

  const loadAdmins = () => api.get("/admins").then(({ data }) => setAdmins(data));

  useEffect(() => {
    loadAdmins();
  }, []);

  const createAdmin = async (e) => {
    e.preventDefault();
    if (!na.email.trim() && !na.username.trim()) {
      toast.error("Indique un correo o un usuario");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: na.name,
        role: na.role,
        password: na.password,
        email: na.email.trim() || null,
        username: na.username.trim() || null,
      };
      await api.post("/admins", payload);
      setNa(EMPTY);
      loadAdmins();
      toast.success("Usuario creado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear el usuario");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/admins/${id}/toggle`);
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar");
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Configuración</h1>
      <p className="text-slate-500 mt-1">Gestión de usuarios del panel</p>

      {/* Create user */}
      <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1"><UserPlus className="h-5 w-5 text-orange-600" /> Crear usuario</h3>
        <p className="text-sm text-slate-500 mb-4">Cree administradores (acceso total) o usuarios de solo lectura (solo pueden ver pacientes y estatus). Puede usar correo o nombre de usuario.</p>
        <form onSubmit={createAdmin} className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Nombre</label>
            <input required value={na.name} onChange={(e) => setNa({ ...na, name: e.target.value })} data-testid="new-admin-name" className="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Rol</label>
            <Select value={na.role} onValueChange={(v) => setNa({ ...na, role: v })}>
              <SelectTrigger data-testid="new-admin-role" className="h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="viewer">Solo lectura</SelectItem>
                <SelectItem value="admin">Administrador (acceso total)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Correo <span className="text-slate-400">(opcional)</span></label>
            <input type="email" value={na.email} onChange={(e) => setNa({ ...na, email: e.target.value })} data-testid="new-admin-email" className="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Usuario <span className="text-slate-400">(opcional)</span></label>
            <input value={na.username} onChange={(e) => setNa({ ...na, username: e.target.value.replace(/\s/g, "") })} data-testid="new-admin-username" className="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Contraseña</label>
            <input required type="password" value={na.password} onChange={(e) => setNa({ ...na, password: e.target.value })} data-testid="new-admin-password" placeholder="Mínimo 6 caracteres" className="w-full h-11 px-3 rounded-lg border border-slate-300 outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600" />
          </div>
          <button type="submit" disabled={creating} data-testid="create-admin-submit" className="sm:col-span-2 h-11 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 disabled:opacity-60 flex items-center justify-center">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear usuario"}
          </button>
        </form>
      </div>

      {/* User list */}
      <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-3"><Users2 className="h-5 w-5 text-orange-600" /> Usuarios del panel</h3>
        <div className="divide-y divide-slate-100">
          {admins.map((a) => {
            const isSelf = (a.email && a.email === user?.email) || (a.username && a.username === user?.username);
            return (
              <div key={a.id} className="flex items-center justify-between py-3 gap-3" data-testid={`admin-${a.email || a.username}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{a.name} {isSelf && <span className="text-xs text-slate-400">(usted)</span>}</p>
                  <p className="text-sm text-slate-500 truncate">{a.email || a.username}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${a.role === "admin" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"}`}>
                    {a.role === "admin" ? <><ShieldCheck className="h-3 w-3" /> Admin</> : <><Eye className="h-3 w-3" /> Solo lectura</>}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${a.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {a.active ? <><Check className="h-3 w-3" /> Activo</> : <><Ban className="h-3 w-3" /> Inactivo</>}
                  </span>
                  {!isSelf && (
                    <button onClick={() => toggle(a.id)} data-testid={`toggle-admin-${a.email || a.username}`} className="text-sm text-orange-600 hover:underline">
                      {a.active ? "Desactivar" : "Activar"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
